// src/pages/AdminPanel.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { apiService } from '../services/api';

const card = {
  background: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  padding: '1.25rem 1.5rem',
  marginBottom: '1.5rem',
};

const sectionTitle = {
  fontSize: '0.95rem',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '1rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const Badge = ({ children, color }) => (
  <span style={{
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: '9999px',
    fontSize: '0.72rem',
    fontWeight: '600',
    background: color === 'green' ? '#d1fae5' : color === 'red' ? '#fee2e2' : color === 'indigo' ? '#e0e7ff' : '#f3f4f6',
    color: color === 'green' ? '#065f46' : color === 'red' ? '#991b1b' : color === 'indigo' ? '#3730a3' : '#374151',
  }}>
    {children}
  </span>
);

const Btn = ({ children, onClick, color = 'gray', disabled, small }) => {
  const colors = {
    gray:   { bg: '#f3f4f6', hover: '#e5e7eb', text: '#374151' },
    red:    { bg: '#fee2e2', hover: '#fecaca', text: '#991b1b' },
    green:  { bg: '#d1fae5', hover: '#a7f3d0', text: '#065f46' },
    indigo: { bg: '#e0e7ff', hover: '#c7d2fe', text: '#3730a3' },
  };
  const c = colors[color] || colors.gray;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: small ? '0.2rem 0.6rem' : '0.4rem 0.9rem',
        fontSize: small ? '0.75rem' : '0.8rem',
        fontWeight: '500',
        border: 'none',
        borderRadius: '6px',
        background: c.bg,
        color: c.text,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
};

const Input = ({ value, onChange, placeholder, type = 'text', style }) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    style={{
      padding: '0.4rem 0.6rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      outline: 'none',
      ...style,
    }}
  />
);

const AdminPanel = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create user form
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Reset password modal
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    apiService.getCurrentUser()
      .then(u => {
        if (!u.is_admin) { navigate('/home'); return; }
        setCurrentUser(u);
        return apiService.listAdminUsers();
      })
      .then(data => { if (data) setUsers(data); })
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    if (!newEmail || !newPassword) { setCreateError('Email and password are required'); return; }
    setCreating(true);
    try {
      const created = await apiService.createAdminUser(newEmail, newPassword, newIsAdmin);
      setUsers(prev => [...prev, created]);
      setNewEmail(''); setNewPassword(''); setNewIsAdmin(false);
    } catch {
      setCreateError('Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (user) => {
    const fn = user.is_active ? apiService.deactivateUser : apiService.activateUser;
    try {
      await fn.call(apiService, user.email);
      setUsers(prev => prev.map(u => u.email === user.email ? { ...u, is_active: !u.is_active } : u));
    } catch (err) {
      alert(err.message || 'Action failed');
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await apiService.deleteAdminUser(email);
      setUsers(prev => prev.filter(u => u.email !== email));
    } catch (err) {
      alert(err.message || 'Delete failed');
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setResetError('');
    if (!resetPassword) { setResetError('Password cannot be empty'); return; }
    try {
      await apiService.resetUserPassword(resetTarget, resetPassword);
      setResetTarget(null);
      setResetPassword('');
    } catch {
      setResetError('Reset failed');
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
        <Navbar />
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem', color: '#6b7280' }}>
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      <Navbar />
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem 1rem' }}>

        <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginBottom: '1.5rem' }}>
          Admin Panel
        </h1>

        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {/* Create user */}
        <div style={card}>
          <div style={sectionTitle}>Create User</div>
          <form onSubmit={handleCreate} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }}>
            <Input
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              placeholder="Email"
              type="email"
              style={{ width: '220px' }}
            />
            <Input
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Password"
              type="password"
              style={{ width: '180px' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem', color: '#374151' }}>
              <input type="checkbox" checked={newIsAdmin} onChange={e => setNewIsAdmin(e.target.checked)} />
              Admin
            </label>
            <Btn color="indigo" disabled={creating}>
              {creating ? 'Creating…' : 'Create'}
            </Btn>
            {createError && <span style={{ color: '#dc2626', fontSize: '0.8rem' }}>{createError}</span>}
          </form>
        </div>

        {/* Users table */}
        <div style={card}>
          <div style={sectionTitle}>Users ({users.length})</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  {['Email', 'Status', 'Role', 'Last Login', 'Analyses', 'API Keys', 'Actions'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#6b7280', fontWeight: '600', fontSize: '0.8rem' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.email} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.6rem 0.75rem', color: '#111827', fontWeight: '500' }}>{u.email}</td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>
                      <Badge color={u.is_active ? 'green' : 'red'}>{u.is_active ? 'Active' : 'Inactive'}</Badge>
                    </td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>
                      {u.is_admin ? <Badge color="indigo">Admin</Badge> : <span style={{ color: '#9ca3af' }}>User</span>}
                    </td>
                    <td style={{ padding: '0.6rem 0.75rem', color: '#6b7280' }}>
                      {u.last_login ? new Date(u.last_login).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '0.6rem 0.75rem', color: '#374151' }}>{u.upload_count}</td>
                    <td style={{ padding: '0.6rem 0.75rem', color: '#374151' }}>{u.api_key_count}</td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                        <Btn
                          small
                          color={u.is_active ? 'red' : 'green'}
                          onClick={() => handleToggleActive(u)}
                          disabled={u.email === currentUser?.email}
                        >
                          {u.is_active ? 'Deactivate' : 'Activate'}
                        </Btn>
                        <Btn small color="gray" onClick={() => { setResetTarget(u.email); setResetPassword(''); setResetError(''); }}>
                          Reset PW
                        </Btn>
                        <Btn
                          small
                          color="red"
                          onClick={() => handleDelete(u.email)}
                          disabled={u.email === currentUser?.email}
                        >
                          Delete
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Reset password modal */}
      {resetTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '1.5rem 2rem', width: '360px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '0.25rem' }}>Reset Password</h2>
            <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>{resetTarget}</p>
            <form onSubmit={handleResetSubmit}>
              <Input
                value={resetPassword}
                onChange={e => setResetPassword(e.target.value)}
                placeholder="New password"
                type="password"
                style={{ width: '100%', marginBottom: '0.75rem' }}
              />
              {resetError && <p style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '0.5rem' }}>{resetError}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <Btn color="gray" onClick={() => setResetTarget(null)}>Cancel</Btn>
                <Btn color="indigo">Reset</Btn>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
